import { useEffect, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fromArrayBuffer } from 'geotiff';

import { AiFillCaretDown, AiOutlineClose } from 'react-icons/ai';

import '../../FileManagerModals.scss';
import './UploadModal.scss';

import { ErrorMessage } from '../ModalComponents';

import { Typography, Input, Button, Select, Modal, Collapse } from 'antd';

import { uploadFileTypeOptions, getExtensionsForFileType, getMaxBytesForFileType, ROOT_DISPLAY_NAME } from '../../../constants.ts';
import { FileNode, SelectedFolder } from '../../../../../types/FileManagerTypes';
import { DraggerModalStep } from '../DraggerModalStep';
import FolderSelectorTree from '../../../../../shared/components/FolderSelectorTree/index.tsx';
import FileManagerService from '../../../../../services/FileManager/fileManager.service';
import { QueryKeys } from '../../../../../enums/queryKeys';
import { PermissionsPicker } from '../../../../../shared/components/PermissionsPicker';
import { usePermissionPrincipals } from '../../../../../hooks/usePermissionPrincipals';
import { PermissionsPickerVariant } from '../../../../../enums/permissionEnums';
import { UploadDestination } from '../../../../../enums/uploadDestination';

const { Title, Text } = Typography;

interface UploadModalProps {
  show: boolean;
  hide: () => void;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>, isDragged?: boolean) => Promise<void>;
  handleFileUpload: (reuploadList?: FileNode[], onSuccess?: () => void, destinationType?: UploadDestination) => Promise<void>;
  /** Resolved root id for the home map. */
  rootFolderId?: string;
  createNewFolder: (folderName: string, currentDirectoryId?: string) => Promise<FileNode | undefined>;
  currentFolderID: string;
  updateUploadFolder: (folderId: string) => void;
  selectedFiles: FileList | File[] | null;
  /** Passed by VFSBrowser but not currently read inside this component. */
  uploadFolder?: string;
  /** True while an upload is in flight — disables the final step's submit button. */
  isUploading?: boolean;
}

const getExtension = (name: string): string => name.slice(name.lastIndexOf('.') + 1).toLowerCase();

const getBaseName = (name: string): string => {
  const lastDot = name.lastIndexOf('.');
  return lastDot === -1 ? name : name.slice(0, lastDot).toLowerCase();
};

export const UploadModal = ({
  show,
  hide,
  handleFileChange,
  handleFileUpload,
  rootFolderId = '',
  createNewFolder,
  currentFolderID,
  updateUploadFolder,
  selectedFiles,
  isUploading,
}: UploadModalProps) => {
  // Home getfilemapping (files + folders), fetched here rather than taken from
  // the browser, whose fileMap is replaced by search/filter results. Same query
  // key as the picker's own fetch, so the two share one request.
  const { getFileMapping } = FileManagerService();
  const { data } = useQuery({ ...getFileMapping(show), staleTime: 0 });
  const fileMap = data?.fileMap ?? {};
  const queryClient = useQueryClient();

  // "Upload to current folder" (US-109511 Scenario 2) is satisfied by
  // defaulting the selection here rather than a separate quick-pick item —
  // the product note explicitly allows condensing it into the picker.
  const getCurrentFolderName = (folderId: string): string => {
    if (!folderId || folderId === rootFolderId) return ROOT_DISPLAY_NAME;
    return fileMap?.[folderId]?.name ?? ROOT_DISPLAY_NAME;
  };

  const [addFileCase, setAddFileCase] = useState<string>('first-prompt');
  const [destinationCase, setDestinationCase] = useState<'new-folder' | 'existing-folder'>('existing-folder');

  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [selectedFileType, setSelectedFileType] = useState<string>('');

  const [folderSelectorOpen, setFolderSelectorOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [errorMessages, setErrorMessages] = useState<string>();

  // Derived, not state: the name resolves once the folder map has loaded.
  const selectedFolderLabel = selectedFolder ? getCurrentFolderName(selectedFolder) : '';

  // useState's initializer only runs on first mount, but UploadModal is
  // always rendered (VFSBrowser just toggles `show`), so it can't be used to
  // seed the default — currentFolderID keeps changing as the user navigates
  // before ever opening this modal. Re-sync on every closed->open transition.
  useEffect(() => {
    if (show) setSelectedFolder(currentFolderID || rootFolderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // GeoTIFF world-file check only — extension/slot validity is derived
  // visually per-file by FileDragger's slot rows, not gated here. Returns the
  // error message (or null) for FileDragger's `validate` prop.
  const validateGeoTIFF = async (files: File[]): Promise<string | null> => {
    const tiffExtensions = ['tif', 'tiff'];
    const requiredTiffWorldExtensions = ['tfw', 'tifw', 'tfwx'];

    async function checkGeoTIFF(file: File) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const tiff = await fromArrayBuffer(arrayBuffer);
        const image = await tiff.getImage();
        const geoKeys = image.getGeoKeys();

        return geoKeys && Object.keys(geoKeys).length > 0;
      } catch (error) {
        console.error('Error reading GeoTIFF metadata:', error);
        return false;
      }
    }

    const groups: Record<string, File[]> = {};
    for (const file of files) {
      const baseName = getBaseName(file.name);
      if (!groups[baseName]) groups[baseName] = [];
      groups[baseName].push(file);
    }

    for (const [baseName, groupFiles] of Object.entries(groups)) {
      const hasWorldFile = groupFiles.some((file) => requiredTiffWorldExtensions.includes(getExtension(file.name)));

      for (const file of groupFiles) {
        if (tiffExtensions.includes(getExtension(file.name))) {
          const isGeoTIFF = await checkGeoTIFF(file);
          if (!isGeoTIFF && !hasWorldFile) {
            return `The TIFF file "${baseName}.tif" must be accompanied by a matching world file (.tfw, .tifw, etc.).`;
          }
        }
      }
    }

    return null;
  };

  // A single, consolidated duplicate-name confirmation shown once at the
  // point of no return, regardless of how many/which order files were
  // dropped in beforehand (handleBeforeUpload only guards within the
  // current selection batch, not the destination's real contents).
  const confirmDuplicateFiles = (label: string): Promise<boolean> =>
    new Promise((resolve) => {
      Modal.confirm({
        title: 'Already exists',
        content: `${label} already exists in the destination folder. Replace it?`,
        okText: 'Replace',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

  const handleUpload = async () => {
    setLoading(true);

    const files = selectedFiles ? Array.from(selectedFiles) : [];
    const destinationNames = new Set(
      (fileMap[selectedFolder]?.childrenIds ?? []).map((id) => fileMap[id]?.name.toLowerCase()).filter((name): name is string => Boolean(name)),
    );
    const hasDuplicate = files.some((f) => destinationNames.has(f.name.toLowerCase()));

    if (hasDuplicate) {
      const label = selectedFileType === 'SHAPEFILE' ? `A shapefile dataset named "${getBaseName(files[0].name)}"` : `"${files[0].name}"`;
      const proceed = await confirmDuplicateFiles(label);
      if (!proceed) {
        setLoading(false);
        return;
      }
    }

    try {
      // Close only on real success — a failure keeps this modal open with
      // selectedFiles intact so the user can retry without reselecting.
      await handleFileUpload(
        undefined,
        () => {
          setDefault();
          hide();
        },
        UploadDestination.EXISTING,
      );
    } catch (error) {
      console.error('Error uploading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const [canSubmitUpload, setCanSubmitUpload] = useState(false);

  // Captured only — no RBAC backend to persist folder permissions against yet.
  const [permissionValues, setPermissionValues] = useState<string[]>([]);
  const { users, groups, isLoading: principalsLoading } = usePermissionPrincipals(show);

  const clearFiles = () => {
    const event = {
      target: { files: new DataTransfer().files },
    } as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(event);
  };

  const setDefault = () => {
    setSelectedFolder(currentFolderID || rootFolderId);
    setNewFolderName('');
    setSelectedFileType('');
    setAddFileCase('first-prompt');
    setErrorMessages('');
    setPermissionValues([]);
    clearFiles();
  };

  const handleCloseModal = () => {
    hide();
    setDefault();
  };

  const handleFolderSelect = (folder: SelectedFolder) => setSelectedFolder(folder.id);

  const handleExistingFolderSubmit = () => {
    setErrorMessages('');

    if (!selectedFolder) {
      setErrorMessages('Please select a folder.');
      return;
    }
    if (!selectedFileType) {
      setErrorMessages('Please select a file type.');
      return;
    }

    updateUploadFolder(selectedFolder);
    setDestinationCase('existing-folder');
    setAddFileCase('upload-file');
  };

  // Sibling folder names from home fileMap (dirs only) — same set the picker shows.
  const getSiblingFolderNames = (parentId: string): string[] =>
    (fileMap[parentId]?.childrenIds ?? []).flatMap((id) => {
      const node = fileMap[id];
      return node?.isDir && node.name ? [node.name] : [];
    });

  const handleNewFolderSubmit = async () => {
    setErrorMessages('');

    if (!selectedFolder) {
      setErrorMessages('Please select a folder.');
      return;
    }
    if (!newFolderName) {
      setErrorMessages('Please enter a folder name.');
      return;
    }
    if (!selectedFileType) {
      setErrorMessages('Please select a file type.');
      return;
    }
    if (
      getSiblingFolderNames(selectedFolder)
        .map((name) => name.toLowerCase())
        .includes(newFolderName.trim().toLowerCase())
    ) {
      setErrorMessages('A folder with this name already exists.');
      return;
    }

    setLoading(true);
    const newFolder = await createNewFolder(newFolderName, selectedFolder);
    setLoading(false);

    if (!newFolder) {
      setErrorMessages('Unable to create folder. Please try again.');
      return;
    }

    // The picker's map predates this folder — refresh it so the new folder is
    // selectable and its (empty) contents back the duplicate-name check.
    // Awaited so the folder-label lookup is already fresh if the user backs
    // out of the dragger step right away, instead of racing the refetch.
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_FOLDERS_FOR_SELECTION] });

    // selectedFolder now points at the new (guaranteed-empty) folder, so the
    // duplicate-file-name check in handleUpload checks the right destination.
    setSelectedFolder(newFolder.id);
    setDestinationCase('new-folder');
    setAddFileCase('upload-file');
  };

  return (
    <>
      <Modal
        open={show && addFileCase !== 'upload-file'}
        onCancel={handleCloseModal}
        centered
        footer={null}
        closable={false}
        width={416}
        classNames={{
          mask: 'upload-modal-mask',
          content: 'upload-modal-content',
        }}
      >
        {(() => {
          switch (addFileCase) {
            case 'first-prompt':
              return (
                <div>
                  <div className="files__header">
                    <Title level={4} className="files__title">
                      Add Files
                    </Title>
                    <a onClick={handleCloseModal} className="files__button">
                      <AiOutlineClose />
                    </a>
                  </div>

                  <div className="first-prompt">
                    <a className="first-prompt__button-group" onClick={() => setAddFileCase('new-folder')}>
                      <img className="default-icon" src="/assets/add-folder-icon.svg" alt="Add to new folder" />
                      <img className="hover-icon" src="/assets/add-folder-icon-active.svg" alt="Add to new folder" />
                      <Text className="files-modal__text first-prompt__text">Add to new folder</Text>
                    </a>
                    <a className="first-prompt__button-group" onClick={() => setAddFileCase('existing-folder')}>
                      <img className="default-icon" src="/assets/add-existing-folder-icon.svg" alt="Add to existing folder" />
                      <img className="hover-icon" src="/assets/save_existing_folder_icon.svg" alt="Add to existing folder" />
                      <Text className="files-modal__text first-prompt__text">Add to existing folder</Text>
                    </a>
                  </div>
                </div>
              );

            case 'existing-folder':
              return (
                <div>
                  <div className="files__header">
                    <Title level={4} className="files__title">
                      Add Files To an Existing Folder
                    </Title>
                    <a onClick={handleCloseModal} className="files__button">
                      <AiOutlineClose />
                    </a>
                  </div>

                  <div className="existing-folder">
                    <div className="upload__form-group">
                      <div className="add-file__form-group">
                        <Button className="add-file__location-trigger" onClick={() => setFolderSelectorOpen(true)}>
                          {selectedFolderLabel || 'Folder Menu'}
                          <AiFillCaretDown />
                        </Button>
                      </div>
                      <div className="add-file__form-group">
                        <Select
                          className="add-file__type-select"
                          popupClassName="upload-modal-dropdown-menu"
                          placement="bottomLeft"
                          placeholder="File Type"
                          suffixIcon={<AiFillCaretDown />}
                          value={selectedFileType || undefined}
                          onChange={(value) => setSelectedFileType(value)}
                          options={uploadFileTypeOptions.map((type) => ({
                            label: type,
                            value: type,
                          }))}
                        />
                      </div>
                      <ErrorMessage error={errorMessages} />
                    </div>

                    <div className="file_button-group">
                      <Button className="add-file__back-button" onClick={setDefault}>
                        Back
                      </Button>
                      <Button type="primary" className="add-file__submit-button" onClick={handleExistingFolderSubmit}>
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              );

            case 'new-folder':
              return (
                <div>
                  <div className="files__header">
                    <Title level={4} className="files__title">
                      Add Files To a New Folder
                    </Title>
                    <a onClick={handleCloseModal} className="files__button">
                      <AiOutlineClose />
                    </a>
                  </div>
                  <div className="new-folder">
                    <div className="upload__form-group">
                      <div className="add-file__form-group">
                        <Button className="add-file__location-trigger" onClick={() => setFolderSelectorOpen(true)}>
                          {selectedFolderLabel || 'Folder Menu'}
                          <AiFillCaretDown />
                        </Button>
                      </div>
                      <div className="add-file__form-group">
                        <Select
                          className="add-file__type-select"
                          popupClassName="upload-modal-dropdown-menu"
                          placement="bottomLeft"
                          placeholder="File Type"
                          suffixIcon={<AiFillCaretDown />}
                          value={selectedFileType || undefined}
                          onChange={(value) => setSelectedFileType(value)}
                          options={uploadFileTypeOptions.map((type) => ({
                            label: type,
                            value: type,
                          }))}
                        />
                      </div>

                      <div className="add-file__form-group">
                        <Collapse
                          className="add-file__permissions"
                          expandIconPosition="end"
                          expandIcon={() => <AiFillCaretDown className="add-file__permissions-caret" />}
                          items={[
                            {
                              key: 'permissions',
                              label: 'Permissions',
                              children: (
                                <PermissionsPicker
                                  users={users}
                                  groups={groups}
                                  selectedValues={permissionValues}
                                  onChange={setPermissionValues}
                                  variant={PermissionsPickerVariant.DARK}
                                  isLoading={principalsLoading}
                                />
                              ),
                            },
                          ]}
                        />
                      </div>

                      <div className="add-file__form-group">
                        <Input className="add-file__input" placeholder="FOLDER NAME" onChange={(e) => setNewFolderName(e.target.value)} />
                      </div>
                    </div>

                    <ErrorMessage error={errorMessages} />

                    <div className="file_button-group">
                      <Button className="add-file__back-button" onClick={setDefault}>
                        Back
                      </Button>
                      <Button type="primary" className="add-file__submit-button" onClick={handleNewFolderSubmit} loading={loading} disabled={loading}>
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              );

            default:
              return null;
          }
        })()}
      </Modal>
      <DraggerModalStep
        open={show && addFileCase === 'upload-file'}
        onClose={handleCloseModal}
        headerTitle={selectedFileType}
        requiredExtensions={getExtensionsForFileType(selectedFileType)}
        isMultiPart={selectedFileType === 'SHAPEFILE'}
        accept={getExtensionsForFileType(selectedFileType)
          .map((ext) => `.${ext}`)
          .join(',')}
        placeholderLabel={selectedFileType}
        selectedFiles={selectedFiles}
        handleFileChange={handleFileChange}
        onValidityChange={setCanSubmitUpload}
        maxBytesFor={() => getMaxBytesForFileType(selectedFileType)}
        validate={validateGeoTIFF}
        errorMessage={errorMessages}
        setErrorMessage={setErrorMessages}
        onBack={() => {
          clearFiles();
          setAddFileCase(destinationCase);
        }}
        onSubmit={handleUpload}
        submitLabel="Add"
        submitDisabled={!!errorMessages || !canSubmitUpload || !!isUploading}
        submitLoading={loading}
      />
      <FolderSelectorTree
        open={folderSelectorOpen}
        onClose={() => setFolderSelectorOpen(false)}
        onSelect={handleFolderSelect}
        rootFolderId={rootFolderId}
        initialFolderId={selectedFolder || currentFolderID}
        variant="dark"
        bodyLabel="SELECT FOLDER LOCATION"
        rootLabel={`Select ${ROOT_DISPLAY_NAME}`}
        primaryLabel="ADD"
      />
    </>
  );
};
